import React, { useState, useEffect } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { getCountryScreeners, getSchoolScreeners } from "../services/APIClient"; // Update import path as needed
import "./AssessmentTable.css";

function AssessmentTable(props) {
  const {
    assessments,
    selectAssessmentId,
    setSelectAssessmentId,
    setSelectAssessmentName,
    classDetails,
    gradeLevelOptions,
    schoolid, // Add schoolId as a prop
  } = props;

  const [screenerSettings, setScreenerSettings] = useState({});
  const [loadingScreeners, setLoadingScreeners] = useState(true);
  const [screenerError, setScreenerError] = useState(null);

  const gradeLevel1 = classDetails.grade_level1;
  const gradeLevel2 = classDetails.grade_level2;

  console.log(schoolid, "schoolId in AssessmentTable");

  const gradeLevel1Ids = new Set([
    "d613e45e-b3b0-4d0f-bcdc-cc17f38bd285",
    "68ce3a92-73e0-4158-9c08-31bff6246d23", 
    "6a43ae18-2251-4876-8f15-23e2c897fdab"
  ]);

  const gradeLevel2Ids = new Set([
    "927b2379-998c-4424-af56-1f82e0144765",
    "9b962947-4f59-4e19-a725-40601b66e8ff",
    "08e97976-f32f-4ad6-a2df-7a2be0789d70",
  ]);

  const assessmentOrder = {
    baseline: 1,
    midpoint: 2,
    "end of year": 3,
  };

  // Fetch screener settings on component mount
  useEffect(() => {
    const fetchScreenerSettings = async () => {
      if (!schoolid) {
        console.error("❌ No schoolId provided to AssessmentTable");
        setLoadingScreeners(false);
        return;
      }

      try {
        setLoadingScreeners(true);
        setScreenerError(null);

        console.log("🔍 Starting screener fetch for schoolId:", schoolid);

        // First try to get school-specific screener settings
        console.log("📞 Calling getSchoolScreeners with schoolId:", schoolid);
        let screeners = await getSchoolScreeners(schoolid);
        console.log("📥 getSchoolScreeners response:", screeners);
        console.log("📊 getSchoolScreeners response type:", typeof screeners);
        console.log("📊 getSchoolScreeners is array:", Array.isArray(screeners));
        console.log("📊 getSchoolScreeners length:", screeners?.length);
        
        // If no school-specific settings found, fallback to country settings
        if (!screeners || screeners.length === 0) {
          console.log("⚠️ No school screeners found, trying country screeners...");
          console.log("📞 Calling getCountryScreeners with schoolId:", schoolid);
          screeners = await getCountryScreeners(schoolid);
          console.log("📥 getCountryScreeners response:", screeners);
          console.log("📊 getCountryScreeners response type:", typeof screeners);
          console.log("📊 getCountryScreeners is array:", Array.isArray(screeners));
          console.log("📊 getCountryScreeners length:", screeners?.length);
        } else {
          console.log("✅ Using school-specific screeners");
        }

        // Convert array of screeners to a map for easy lookup
        const screenerMap = {};
        if (screeners && Array.isArray(screeners)) {
          console.log("🔄 Converting screeners array to map...");
          screeners.forEach((screener, index) => {
            console.log(`📋 Screener ${index}:`, screener);
            console.log(`  - assessmentid: ${screener.assessmentid}`);
            console.log(`  - active: ${screener.active}`);
            screenerMap[screener.assessmentid] = screener.active;
          });
        } else {
          console.log("⚠️ Screeners is not a valid array:", screeners);
        }

        console.log("🗺️ Final screener map:", screenerMap);
        setScreenerSettings(screenerMap);
        console.log("✅ Screener settings updated successfully");

      } catch (error) {
        console.error("❌ Error fetching screener settings:", error);
        console.error("❌ Error name:", error.name);
        console.error("❌ Error message:", error.message);
        console.error("❌ Error stack:", error.stack);
        setScreenerError(error);
        // Default to all screeners disabled on error
        setScreenerSettings({});
      } finally {
        setLoadingScreeners(false);
        console.log("🏁 Screener loading completed");
      }
    };

    fetchScreenerSettings();
  }, [schoolid]);

  const sortAssessments = (a, b) => {
    const getSortKey = (name) => {
      name = name.toLowerCase();
      if (name.includes("baseline")) return assessmentOrder["baseline"];
      if (name.includes("midpoint")) return assessmentOrder["midpoint"];
      if (name.includes("end of year")) return assessmentOrder["end of year"];
      return 4;
    };

    let aOrder = getSortKey(a.name);
    let bOrder = getSortKey(b.name);
    return aOrder - bOrder;
  };

  const gradeAbbreviations = {
    "Standard 1": "S1",
    "Standard 2": "S2",
    Reception: "Recep",
    "Year 1": "Y1",
    "Primary 1": "P1",
    "Primary 2": "P2",
    "Pre-K": "Pre-K",
    "Kindergarten (K)": "K",
    "KG1 (Kindergarten 1)": "KG1",
    "KG2 (Kindergarten 2)": "KG2",
    "Junior Infants": "Jnr",
    "Senior Infants": "Snr",
  };

  let grade1Abbrev = gradeAbbreviations[gradeLevelOptions[0]?.grade1];
  let grade2Abbrev = gradeAbbreviations[gradeLevelOptions[0]?.grade2];

  const replacePrefix = (name) => {
    if (gradeLevel1) {
      return name.replace("Junior", grade1Abbrev || "Grade1");
    } else if (gradeLevel2) {
      return name.replace("Senior", grade2Abbrev || "Grade2");
    }
    return name;
  };

  // Helper function to check if an assessment is enabled based on screener settings
  const isAssessmentEnabled = (assessmentId) => {
    // If still loading or error occurred, default to disabled
    if (loadingScreeners || screenerError) {
      console.log(`🔒 Assessment ${assessmentId} disabled due to loading/error state`);
      return false;
    }
    
    // Return the active status from screener settings, default to false if not found
    const isEnabled = screenerSettings[assessmentId] === true;
    console.log(`🔍 Assessment ${assessmentId} enabled status:`, isEnabled);
    console.log(`🔍 Screener setting value:`, screenerSettings[assessmentId]);
    return isEnabled;
  };

  let assessmentsArray = [];

  if (gradeLevel1) {
    assessments.forEach((assessment) => {
      if (gradeLevel1Ids.has(assessment.id)) {
        let imageName;

        if (assessment.name.toLowerCase().includes("baseline")) {
          imageName = "autumn";
        } else if (assessment.name.toLowerCase().includes("midpoint")) {
          imageName = "spring";
        } else if (assessment.name.toLowerCase().includes("end of year")) {
          imageName = "summer";
        } else {
          imageName = assessment.name.split(" ")[0];
        }

        assessmentsArray.push({
          ...assessment,
          name: replacePrefix(assessment.name, "Junior"),
          imageName: imageName.toLowerCase(),
        });
      }
    });
    assessmentsArray.sort(sortAssessments);
  }

  if (gradeLevel2) {
    assessments.forEach((assessment) => {
      if (gradeLevel2Ids.has(assessment.id)) {
        let imageName;

        if (assessment.name.toLowerCase().includes("baseline")) {
          imageName = "autumn";
        } else if (assessment.name.toLowerCase().includes("midpoint")) {
          imageName = "spring";
        } else if (assessment.name.toLowerCase().includes("end of year")) {
          imageName = "summer";
        } else {
          imageName = assessment.name.split(" ")[0];
        }

        assessmentsArray.push({
          ...assessment,
          name: replacePrefix(assessment.name, "Senior"),
          imageName: imageName.toLowerCase(),
        });
      }
    });
    assessmentsArray.sort(sortAssessments);
  }

  console.log("📋 Final assessments array:", assessmentsArray);
  console.log("📋 Assessments count:", assessmentsArray.length);

  // Auto-select the first enabled assessment when component loads
  useEffect(() => {
    if (!selectAssessmentId && assessmentsArray.length > 0 && !loadingScreeners) {
      console.log("🎯 Auto-selecting first enabled assessment...");
      // Find the first enabled assessment in order of priority
      const enabledAssessments = assessmentsArray.filter(assessment => 
        isAssessmentEnabled(assessment.id)
      );

      console.log("✅ Enabled assessments:", enabledAssessments);
      console.log("✅ Enabled assessments count:", enabledAssessments.length);

      if (enabledAssessments.length > 0) {
        const firstEnabled = enabledAssessments[0];
        console.log("🎯 Auto-selecting assessment:", firstEnabled);
        setSelectAssessmentId(firstEnabled.id);
        setSelectAssessmentName(firstEnabled.name);
      } else {
        console.log("⚠️ No enabled assessments found for auto-selection");
      }
    }
  }, [
    selectAssessmentId,
    assessmentsArray,
    setSelectAssessmentId,
    setSelectAssessmentName,
    loadingScreeners,
    screenerSettings,
  ]);

  // Show loading state
  if (loadingScreeners) {
    return (
      <Row className="margin-left">
        <Col>
          <div>Loading assessment settings...</div>
        </Col>
      </Row>
    );
  }

  // Show error state
  if (screenerError) {
    return (
      <Row className="margin-left">
        <Col>
          <div>Error loading assessment settings. Please try again.</div>
        </Col>
      </Row>
    );
  }

  return (
    <Row className="margin-left">
      <Col>
        <div>
          {assessmentsArray.map((assessment, index) => {
            const isEnabled = isAssessmentEnabled(assessment.id);
            console.log(`🔘 Rendering assessment ${assessment.name} (${assessment.id}): enabled=${isEnabled}`);

            return (
              <button
                key={index}
                className={`light-blue-button assessment-button ${
                  selectAssessmentId === assessment.id ? "selected" : ""
                } ${!isEnabled ? "disabled" : ""}`}
                onClick={() => {
                  if (isEnabled) {
                    console.log("👆 Assessment clicked:", assessment.name, assessment.id);
                    setSelectAssessmentId(assessment.id);
                    setSelectAssessmentName(assessment.name);
                  } else {
                    console.log("🚫 Disabled assessment clicked:", assessment.name);
                  }
                }}
              >
                {assessment.name}{" "}
                <img
                  src={`/assets/imgs/${assessment.imageName}-icon.png`}
                  className="season-assessment-icon"
                  alt={assessment.name}
                />
                {!isEnabled && (
                  <div className="small-text" style={{ fontSize: "16px" }}>
                    Currently Disabled
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </Col>
    </Row>
  );
}

export default AssessmentTable;